<template>
    <div>
        <div class="form-group form-icon">
            <button type="button" :id="uuid" name="dates"
                    :class="['portal-daterange-trigger inline-flex items-center py-2 pl-10 pr-4 w-full text-sm text-gray-500 bg-white border border-gray-300 transition-shadow hover:bg-gray-50 focus:outline-none focus:ring-0 focus:border-gray-400', corners]">
                <span v-if="isAllTimeSelected" class="text-gray-700">All time</span>
                <template v-else>
                    <span class="text-gray-700">{{ _fromDate }}</span>
                    <span class="px-1">-</span>
                    <span class="text-gray-700">{{ _toDate }}</span>
                </template>
            </button>
            <input type="hidden" name="date_to" :value="_toDate">
            <input type="hidden" name="date_from" :value="_fromDate">
        </div>
    </div>
</template>

<script>
import daterangepickerCss from 'daterangepicker/daterangepicker.css'
import daterangepicker from 'daterangepicker'
import moment from 'moment-timezone'
import {v4 as uuid} from "uuid";
import {usePortalContentAndDesignStore} from "../../stores/partners/portalContentAndDesignStore";

export const DEFAULT_RANGES = {
    'All time': [moment('1900-01-01'), moment().endOf('day')], // Will be updated dynamically
    'Today': [moment().startOf('day'), moment().endOf('day')],
    'Yesterday': [moment().subtract(1, 'days').startOf('day'), moment().subtract(1, 'days').endOf('day')],
    'Last 7 Days': [moment().subtract(6, 'days').startOf('day'), moment().endOf('day')],
    'Last 30 Days': [moment().subtract(29, 'days').startOf('day'), moment().endOf('day')],
    'This Month': [moment().startOf('month'), moment().endOf('month')],
    'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
}

export default {
    components: {},
    props: ['date_from', 'date_to', 'ranges'],
    data() {
        const startEndDates = this.getStartEndDates();
        const isAllTime = !this.date_from && !this.date_to;
        return {
            uuid: uuid(),
            toDate: startEndDates.to,
            fromDate: startEndDates.from,
            _toDate: this.formattedDate(startEndDates.to),
            _fromDate: this.formattedDate(startEndDates.from),
            isAllTimeSelected: isAllTime
        }
    },
    computed: {
        partnerSignupDate() {
            // Access partner data from the Pinia store
            try {
                const portalStore = usePortalContentAndDesignStore();
                if (portalStore?.authUser?.created_at) {
                    return moment(portalStore.authUser.created_at);
                }
            } catch (e) {
                console.warn('Could not access partner signup date:', e);
            }
            return moment('1900-01-01'); // Fallback to distant past
        },
        customRanges() {
            const ranges = { ...DEFAULT_RANGES };
            // Update "All time" to use partner signup date to today (end of day)
            ranges['All time'] = [this.partnerSignupDate, moment().endOf('day')];
            return this.ranges || ranges;
        },
        corners() {
            try {
                const portalStore = usePortalContentAndDesignStore();
                return portalStore.getPortalLayoutCornersClass || 'rounded';
            } catch (e) {
                return 'rounded';
            }
        },
        isDarkTheme() {
            try {
                return usePortalContentAndDesignStore().getPortalLayoutIsDarkBackground;
            } catch (e) {
                return false;
            }
        },
        elevatedSurface() {
            try {
                return usePortalContentAndDesignStore().getPortalLayoutElevatedSurface;
            } catch (e) {
                return null;
            }
        }
    },
    watch: {
        _toDate(){
            this.emitDatepickerUpdate()
        },
        _fromDate(){
            this.emitDatepickerUpdate()
        },
        isDarkTheme(isDark) {
            const picker = $('#' + this.uuid).data('daterangepicker');
            if (picker?.container) {
                picker.container.toggleClass('daterangepicker--dark', !!isDark);
                this.applyDarkSurface(picker);
            }
        }
    },
    mounted() {
        this.iniDatePicker();
    },
    methods: {
        iniDatePicker() {
            const self = this;
            
            // Check if we should default to "All time"
            const isAllTime = !this.date_from && !this.date_to;
            let startDate = this.fromDate;
            let endDate = this.toDate;
            
            const ranges = this.customRanges;
            
            // Set initial range selection to "All time" if no dates provided
            let initialStartDate = startDate;
            let initialEndDate = endDate;
            let initialLabel = null;
            
            if (isAllTime) {
                initialStartDate = ranges['All time'][0];
                initialEndDate = ranges['All time'][1];
                initialLabel = 'All time';
            }
            
            $('#' + this.uuid).daterangepicker({
                'startDate': initialStartDate,
                'endDate': initialEndDate,
                'locale': {
                    'format': 'YYYY-MM-DD',
                    'separator': ' - ',
                    'firstDay': 1,
                },
                ranges: ranges,
                alwaysShowCalendars: true,
                autoApply: true
            }, (start, end, label) => {
                // Handle "All time" selection
                if (label === 'All time') {
                    this.isAllTimeSelected = true;
                    this._toDate = '';
                    this._fromDate = '';
                } else {
                    this.isAllTimeSelected = false;
                    this._toDate = this.formattedDate(end);
                    this._fromDate = this.formattedDate(start);
                }
            }).on('apply.daterangepicker', function (ev, picker) {
                self.$emit('update', ev, picker);
            });

            // The popup is appended outside the dark-themed content scope, so tag its
            // container with a dark modifier class to style it for dark backgrounds.
            if (this.isDarkTheme) {
                const picker = $('#' + this.uuid).data('daterangepicker');
                if (picker?.container) {
                    picker.container.addClass('daterangepicker--dark');
                    this.applyDarkSurface(picker);
                }
            }
            
            // Set the label after initialization if it's "All time"
            if (initialLabel === 'All time') {
                const picker = $('#' + this.uuid).data('daterangepicker');
                picker.chosenLabel = 'All time';
                picker.updateElement();
                
                // Show months that contain the selected date range
                // Left calendar shows the start date's month, right calendar shows end date's month
                picker.leftCalendar.month = moment(initialStartDate);
                picker.rightCalendar.month = moment(initialEndDate);
                
                // If start and end are in the same month, show start month and next month
                if (initialStartDate.format('YYYY-MM') === initialEndDate.format('YYYY-MM')) {
                    picker.rightCalendar.month = moment(initialStartDate).add(1, 'month');
                }
                
                picker.updateCalendars();
            }
        },
        applyDarkSurface(picker) {
            // Feed the theme-derived elevated surface to the popup so it matches the
            // active dark theme (near-black for Onyx, navy for Midnight) instead of slate.
            const el = picker?.container?.[0];
            if (el && this.elevatedSurface) {
                el.style.setProperty('--drp-surface', this.elevatedSurface);
            }
        },
        formattedDate(mDate) {
            return mDate ? mDate.format('YYYY-MM-DD') : ''
        },
        getStartEndDates() {
            // Default to "All time" if no dates are provided (partner signup to end of today)
            const defaultToDate = moment().endOf('day');
            const defaultFromDate = this.partnerSignupDate;

            const _tDate = this.date_to ? moment(this.date_to, moment.ISO_8601, true) : defaultToDate;
            const _fDate = this.date_from ? moment(this.date_from, moment.ISO_8601, true) : defaultFromDate;

            return {
                'to': _tDate && _tDate.isValid() ? _tDate : defaultToDate,
                'from': _fDate && _fDate.isValid() ? _fDate : defaultFromDate
            };
        },
        emitDatepickerUpdate() {
            if (this.isAllTimeSelected) {
                this.$emit('date-changed', {dateFrom: null, dateTo: null})
            } else {
                this.$emit('date-changed', {dateFrom: this._fromDate, dateTo: this._toDate})
            }
        }
    }
}
</script>

<style scoped>
/* Just override colors, keep original daterangepicker styling */
:deep(.daterangepicker) {
    color: black;
}
</style>

<style>
/* Global override - just replace the color codes, keep everything else the same */

/* Replace #08c with black equivalent */
.daterangepicker .ranges li.active {
    background-color: #374151 !important;
    color: #fff;
}

.daterangepicker .ranges li.active:hover,
.daterangepicker .ranges li.active:focus {
    background-color: #374151 !important;
    color: #fff;
}

/* Replace #357ebd with black equivalent, keep original styling */
.daterangepicker td.active, 
.daterangepicker td.active:hover {
    background-color: #374151 !important;
    border-color: transparent;
    color: #fff;
}

/* Replace #ebf4f8 with light gray equivalent, keep original styling */
.daterangepicker td.in-range {
    background-color: #f3f4f6 !important;
    border-color: transparent;
    color: #000;
    border-radius: 0;
}

/* Dark theme variant: the popup is appended to the body, so it is styled
   explicitly here rather than through the .portal-content-dark scope. */
.daterangepicker--dark {
    background-color: var(--drp-surface, #1f2937);
    border-color: rgba(255, 255, 255, 0.12);
    color: #e5e7eb;
}

.daterangepicker--dark:after {
    border-bottom-color: var(--drp-surface, #1f2937);
}

.daterangepicker--dark:before {
    border-bottom-color: rgba(255, 255, 255, 0.12);
}

.daterangepicker--dark .calendar-table {
    background-color: var(--drp-surface, #1f2937);
    border-color: var(--drp-surface, #1f2937);
}

.daterangepicker--dark td,
.daterangepicker--dark th {
    color: #e5e7eb;
}

.daterangepicker--dark td.available:hover,
.daterangepicker--dark th.available:hover {
    background-color: rgba(255, 255, 255, 0.08);
}

.daterangepicker--dark td.off,
.daterangepicker--dark td.off.in-range,
.daterangepicker--dark td.off.start-date,
.daterangepicker--dark td.off.end-date {
    background-color: var(--drp-surface, #1f2937);
    color: #6b7280;
}

.daterangepicker--dark td.in-range {
    background-color: rgba(255, 255, 255, 0.08) !important;
    color: #e5e7eb;
}

.daterangepicker--dark .ranges li {
    color: #e5e7eb;
}

.daterangepicker--dark .ranges li:hover {
    background-color: rgba(255, 255, 255, 0.08);
}

.daterangepicker--dark .drp-buttons {
    border-top-color: rgba(255, 255, 255, 0.12);
}

.daterangepicker--dark .drp-calendar.left {
    border-left-color: rgba(255, 255, 255, 0.12);
}

.daterangepicker--dark select.hourselect,
.daterangepicker--dark select.minuteselect,
.daterangepicker--dark select.secondselect,
.daterangepicker--dark select.ampmselect {
    background-color: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.12);
    color: #e5e7eb;
}
</style>
